import React, { useState, useRef, useEffect } from 'react';
import RangeDatePicker from '../Layout/DatePicker.tsx';
import { format } from 'date-fns';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';

// --- Icons ---
// Reuse CloseIcon or import from a shared location if available
const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '20px', height: '20px' }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const CalendarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '18px', height: '18px', marginRight: '8px', color: '#6b7280' }}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z" />
    </svg>
);

// --- Interfaces ---
interface ExportConfigModalProps {
  selectedFormat: 'CSV' | 'Excel' | 'PDF';
  onClose: () => void;
  onBack: () => void;
  reportTitle?: string;
  // onExport: (config: ExportConfig) => void; // Define ExportConfig later
}

// --- Styles --- (Simplified & adapted from ExportReportModal)
const inputBaseStyle: React.CSSProperties = {
    border: '1px solid #d1d5db', // Gray border
    borderRadius: '6px',
    padding: '8px 12px',
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box', // Include padding and border in the element's total width and height
};

const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151', // Medium gray
};

const hintStyle: React.CSSProperties = {
    marginTop: '4px',
    fontSize: '12px',
    color: '#6b7280', // Lighter gray
};

const sectionTitleStyle: React.CSSProperties = {
    marginTop: '24px',
    marginBottom: '16px',
    fontSize: '16px',
    fontWeight: 500,
    color: '#111827',
};

const checkboxContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start', // Align items top for multi-line descriptions
    gap: '10px',
    padding: '12px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    marginBottom: '12px',
    cursor: 'pointer', // Make area clickable
};

const checkboxStyle: React.CSSProperties = {
    marginTop: '2px', // Align checkbox slightly better with text
    width: '16px',
    height: '16px',
    accentColor: '#1f2937', // Style the check color
};

// --- NEW Styles for custom select ---
const selectContainerStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
};

// Style for the select element itself
const selectStyle: React.CSSProperties = {
  ...inputBaseStyle,
  appearance: 'none',
  paddingRight: '36px', // Space for the icon
  background: 'none', // Remove the old SVG background
};

// Style for the dropdown arrow icon
const selectIconStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    right: '12px', // Position on the right
    transform: 'translateY(-50%)',
    pointerEvents: 'none', // Icon should not be clickable
    color: '#6b7280', // Gray icon
    width: '20px', // Icon size
    height: '20px', // Icon size
};

// --- Component ---
const ExportConfigModal: React.FC<ExportConfigModalProps> = ({ selectedFormat, onClose, onBack, reportTitle }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [filename, setFilename] = useState(() => {
      const base = reportTitle ? reportTitle.toLowerCase().replace(/\s+/g, '-') : 'custom-report';
      return `${base}-${new Date().toISOString().split('T')[0]}`;
  });
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([new Date(), new Date()]);
  const [startDate, endDate] = dateRange;
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [includeTransactions, setIncludeTransactions] = useState(true);
  const [includeSummary, setIncludeSummary] = useState(true);
  const [includePartnerInfo, setIncludePartnerInfo] = useState(false);
  const [encoding, setEncoding] = useState('UTF-8');
  const [delimiter, setDelimiter] = useState('Comma (,)');

  // Handle clicks outside the modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleExport = () => {
    console.log('Exporting with config:', {
        filename,
        format: selectedFormat,
        startDate: startDate ? format(startDate, 'yyyy-MM-dd') : null,
        endDate: endDate ? format(endDate, 'yyyy-MM-dd') : null,
        includeHeaders,
        includeTransactions,
        includeSummary,
        includePartnerInfo,
        encoding,
        delimiter,
    });
    onClose(); // Close modal after initiating export
  };

  const handleDateChange = (update: [Date | null, Date | null]) => {
    setDateRange(update);
  };

  const getFileExtension = () => {
      switch (selectedFormat) {
          case 'Excel': return '.xlsx';
          case 'PDF': return '.pdf';
          case 'CSV':
          default: return '.csv';
      }
  }

  return (
    // Backdrop (same as ExportReportModal)
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
      {/* Modal Content */}
      <div
        ref={modalRef}
        style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', width: '100%', maxWidth: '700px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        {/* Modal Header (same as ExportReportModal) */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>Export {reportTitle || 'Custom Report'}</h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>Configure your export options to download the report data</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#6b7280', borderRadius: '50%', display: 'flex' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
            <CloseIcon />
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '24px 32px', maxHeight: '70vh', overflowY: 'auto' }}>
          <h3 style={{ marginTop: 0, marginBottom: '24px', fontSize: '16px', fontWeight: 500, color: '#111827' }}>Configure Export Options</h3>

          {/* Filename */}
          <div>
              <label htmlFor="filename" style={labelStyle}>Filename</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                 <input
                    type="text"
                    id="filename"
                    value={filename}
                    onChange={(e) => setFilename(e.target.value)}
                    style={{ ...inputBaseStyle, flexGrow: 1 }}
                 />
                 <span style={{ color: '#6b7280', fontSize: '14px' }}>{getFileExtension()}</span>
              </div>
              <p style={hintStyle}>Name for the exported file</p>
          </div>

          {/* Date Range (Placeholder) */}
          <div style={{ marginTop: '20px' }}>
              <label htmlFor="daterange" style={labelStyle}>Date Range</label>
              <RangeDatePicker
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateChange}
              />
              <p style={hintStyle}>Select the date range for the report data</p>
          </div>

          {/* Options Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', marginTop: '24px' }}>

            {/* Content Options */}
            <div>
                <h4 style={sectionTitleStyle}>Content Options</h4>
                {/* Include Headers */}
                 <label htmlFor="includeHeaders" style={checkboxContainerStyle}>
                    <input type="checkbox" id="includeHeaders" checked={includeHeaders} onChange={(e) => setIncludeHeaders(e.target.checked)} style={checkboxStyle} />
                    <div>
                        <span style={{ fontWeight: 500, color: '#111827' }}>Include Headers</span>
                        <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#6b7280' }}>Add column headers to the exported data</p>
                    </div>
                 </label>
                {/* Include Transaction Details */}
                 <label htmlFor="includeTransactions" style={checkboxContainerStyle}>
                    <input type="checkbox" id="includeTransactions" checked={includeTransactions} onChange={(e) => setIncludeTransactions(e.target.checked)} style={checkboxStyle} />
                    <div>
                        <span style={{ fontWeight: 500, color: '#111827' }}>Include Transaction Details</span>
                        <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#6b7280' }}>Include detailed transaction information</p>
                    </div>
                 </label>
                {/* Include Summary */}
                 <label htmlFor="includeSummary" style={checkboxContainerStyle}>
                    <input type="checkbox" id="includeSummary" checked={includeSummary} onChange={(e) => setIncludeSummary(e.target.checked)} style={checkboxStyle} />
                    <div>
                        <span style={{ fontWeight: 500, color: '#111827' }}>Include Summary</span>
                        <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#6b7280' }}>Add summary statistics at the beginning</p>
                    </div>
                 </label>
            </div>

            {/* Additional Options */}
            <div>
                <h4 style={sectionTitleStyle}>Additional Options</h4>
                 {/* Include Partner Information */}
                 <label htmlFor="includePartnerInfo" style={checkboxContainerStyle}>
                    <input type="checkbox" id="includePartnerInfo" checked={includePartnerInfo} onChange={(e) => setIncludePartnerInfo(e.target.checked)} style={checkboxStyle} />
                    <div>
                        <span style={{ fontWeight: 500, color: '#111827' }}>Include Partner Information</span>
                        <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#6b7280' }}>Add partner details to the report</p>
                    </div>
                 </label>

                {/* Encoding (Only for CSV/Excel?) */}
                {(selectedFormat === 'CSV' || selectedFormat === 'Excel') ? (
                    <div style={{ marginTop: '20px' }}>
                        <label htmlFor="encoding" style={labelStyle}>Encoding</label>
                        {/* NEW: Wrapper div */}
                        <div style={selectContainerStyle}>
                            <select
                                id="encoding"
                                value={encoding}
                                onChange={(e) => setEncoding(e.target.value)}
                                // Use the new select style
                                style={selectStyle}
                            >
                                <option>UTF-8</option>
                                <option>UTF-16</option>
                                <option>ISO-8859-1</option>
                            </select>
                             {/* NEW: Add the icon */}
                            <ChevronUpDownIcon style={selectIconStyle} />
                        </div>
                        <p style={hintStyle}>Character encoding for the file</p>
                    </div>
                ) : null}

                {/* Delimiter (Only for CSV?) */}
                {selectedFormat === 'CSV' ? (
                    <div style={{ marginTop: '20px' }}>
                        <label htmlFor="delimiter" style={labelStyle}>Delimiter</label>
                         {/* NEW: Wrapper div */}
                        <div style={selectContainerStyle}>
                            <select
                                id="delimiter"
                                value={delimiter}
                                onChange={(e) => setDelimiter(e.target.value)}
                                 // Use the new select style
                                style={selectStyle}
                            >
                                <option>Comma (,)</option>
                                <option>Semicolon (;)</option>
                                <option>Tab (\t)</option>
                                <option>Pipe (|)</option>
                            </select>
                             {/* NEW: Add the icon */}
                             <ChevronUpDownIcon style={selectIconStyle} />
                        </div>
                         <p style={hintStyle}>Character to separate values</p>
                    </div>
                ) : null}
            </div>

          </div>
        </div>

        {/* Modal Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
           <button
            onClick={onBack}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: '1px solid #d1d5db', // Light gray border
              backgroundColor: 'white',
              color: '#374151', // Medium gray text
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background-color 0.2s ease-in-out',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'} // Slightly lighter gray on hover
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
          >
            Back
          </button>
          <button
            onClick={handleExport}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#1f2937', // Dark background
              color: 'white',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background-color 0.2s ease-in-out',
            }}
             onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'} // Slightly lighter on hover
             onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
          >
            Export Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportConfigModal;
